import type { L10n } from "../lib/locales"
import type { Service } from "./services"

export const brandingService: Service = {
  slug: "branding",
  icon: "branding",
  tier: "core",
  title: { ar: "الهوية التجارية والرقمية", en: "Branding & Visual Identity" },
  summary: {
    ar: "نبني هوية متكاملة لشركتك، من استراتيجية العلامة وتصميم الشعار إلى التغليف والتطبيقات الرقمية ودليل الاستخدام.",
    en: "A complete identity for your business, from brand strategy and logo design to packaging, digital touchpoints and brand guidelines.",
  },
  heroLead: {
    ar: "نفهم شركتك وجمهورك، ثم نحوّل ما يميّزك إلى هوية واضحة ومتسقة؛ في شعارك، وعلى منتجاتك، وفي كل نقطة تواصل مع عملائك.",
    en: "We understand your business and audience, then turn what makes you different into a clear, consistent identity. On your logo, your products and every customer touchpoint.",
  },
  problem: {
    ar: "عندما تختلف صورة شركتك من قناة لأخرى، يصعب على العملاء تمييزها. تحتاج العلامة إلى فكرة واضحة ونظام تصميم يجمع كل تطبيقاتها.",
    en: "When your business looks different on every channel, it becomes harder to recognize. A clear idea and a connected design system bring those touchpoints together.",
  },
  approach: {
    ar: "نبدأ بالبحث والاستراتيجية، ثم نصمم الهوية ونختبرها على تطبيقات واقعية، ونسلّم ملفات منظمة ودليلًا يساعد فريقك على استخدامها.",
    en: "We start with research and strategy, design and test the identity on practical applications, then deliver organized files and guidelines your team can use.",
  },
  capabilities: [
    { ar: "بحث واستراتيجية العلامة التجارية", en: "Brand research and strategy" },
    { ar: "تصميم الشعار ونظام الهوية البصرية", en: "Logo design and visual identity system" },
    { ar: "تصميم العبوات ومخططات التغليف", en: "Packaging design and layouts" },
    { ar: "تطبيقات الهوية التجارية والرقمية", en: "Physical and digital brand touchpoints" },
    { ar: "دليل الهوية وملفات التصميم النهائية", en: "Brand guidelines and final design files" },
  ],
  useCases: [
    { ar: "شركة جديدة تؤسس علامتها وحضورها الرقمي.", en: "A new business establishing its brand and digital presence." },
    { ar: "شركة قائمة تطوّر هويتها لتناسب مرحلة نمو جديدة.", en: "An established business refreshing its identity for its next chapter." },
    { ar: "علامة منتجات تحتاج إلى تغليف متسق عبر منتجاتها.", en: "A product brand creating a consistent packaging range." },
  ],
  faqs: [
    {
      q: { ar: "هل يمكن تطوير هويتنا الحالية بدلًا من البدء من جديد؟", en: "Can you refresh our existing identity?" },
      a: { ar: "نعم. نراجع ما يعمل في هويتك الحالية وما يحتاج إلى تطوير، ثم نتفق على نطاق التحديث مع الحفاظ على العناصر التي يعرفها جمهورك.", en: "Yes. We review what works and what needs to evolve, then agree a scope that preserves the elements your audience already recognizes." },
    },
    {
      q: { ar: "هل يمكن طلب تصميم شعار فقط؟", en: "Can we commission just a logo?" },
      a: { ar: "نعم. نحدد معك نطاق الخدمة، سواء كان شعارًا مع قواعد استخدام أساسية أو نظام هوية متكاملًا يشمل التطبيقات والتغليف.", en: "Yes. We agree the scope with you, from a logo with essential usage rules to a full identity with touchpoints and packaging." },
    },
    {
      q: { ar: "هل تشمل الخدمة تصميم العبوات والطباعة؟", en: "Does the service include packaging and printing?" },
      a: { ar: "يمكن أن يشمل النطاق تصميم العبوات وتوزيع عناصر الهوية على مخطط القص والطي المعتمد من المورد. نجهّز ملفات التصميم وفق مواصفات الطباعة المتفق عليها؛ أما التصنيع والطباعة الفعلية فيُتفق عليهما بشكل منفصل.", en: "The scope can include packaging artwork on a supplier-approved dieline. We prepare design files to agreed print specifications; physical printing and manufacturing are arranged separately." },
    },
    {
      q: { ar: "ما الملفات التي نحصل عليها عند التسليم؟", en: "What files will we receive?" },
      a: { ar: "نحدد قائمة التسليم منذ البداية. يمكن أن تشمل ملفات الشعار المتجهة وصيغ الاستخدام الرقمي والطباعة، وملفات التطبيقات المتفق عليها، ودليل الهوية. نوضح كذلك تراخيص الخطوط والصور وأي موارد تحتاج إلى ترخيص مستقل.", en: "We agree the delivery list at the start. It can include vector logos, digital and print exports, agreed application files and brand guidelines. We also document font and image licenses and any resources that need a separate license." },
    },
    {
      q: { ar: "كيف تُحدد مدة المشروع والمراجعات؟", en: "How are timing and revisions agreed?" },
      a: { ar: "بعد جلسة التعارف نحدد المخرجات والجدول ونقاط المراجعة وعدد جولات التعديل في العرض. تُراجع كل مرحلة معك قبل الانتقال إلى المرحلة التالية.", en: "After discovery, the proposal sets out deliverables, timing, review checkpoints and revision rounds. We review each stage with you before moving to the next." },
    },
  ],
  relatedSolutions: ["ecommerce", "marketing-agencies"],
}

export const brandingStages: { title: L10n; body: L10n; deliverable: L10n }[] = [
  {
    title: { ar: "الاكتشاف والبحث", en: "Discovery & Research" },
    body: { ar: "جلسة لفهم أعمالك وطموحاتك وجمهورك، مع دراسة السوق والمنافسين ومراجعة حضورك الحالي.", en: "A conversation about your business, ambitions and audience, supported by market research, competitor review and an audit of your current presence." },
    deliverable: { ar: "ملخص المشروع ونتائج البحث", en: "Project brief and research findings" },
  },
  {
    title: { ar: "استراتيجية العلامة", en: "Brand Strategy" },
    body: { ar: "نحدد تموضع العلامة وقيمها وشخصيتها ورسالتها ونبرة خطابها، لنرسم أساسًا واضحًا لقرارات التصميم.", en: "We define your positioning, values, personality, key messages and tone of voice to give every design decision a clear foundation." },
    deliverable: { ar: "استراتيجية العلامة والاتجاه الإبداعي", en: "Brand strategy and creative direction" },
  },
  {
    title: { ar: "تصميم الهوية البصرية", en: "Visual Identity Design" },
    body: { ar: "نطوّر اتجاهات للشعار، ثم نبني الألوان والخطوط والعناصر البصرية حول الاتجاه المعتمد ونختبر وضوحها في أحجام واستخدامات مختلفة.", en: "We explore logo directions, then build colors, typography and graphic elements around the approved concept and test them at different sizes and in different contexts." },
    deliverable: { ar: "الشعار ونظام الهوية البصرية", en: "Logo and visual identity system" },
  },
  {
    title: { ar: "تطبيقات العلامة", en: "Brand Touchpoints" },
    body: { ar: "نحوّل النظام البصري إلى تطبيقات تناسب عملك؛ من المطبوعات والعبوات إلى القوالب الاجتماعية والواجهات الرقمية.", en: "We put the system to work across the touchpoints your business needs, from stationery and packaging to social templates and digital interfaces." },
    deliverable: { ar: "تصميم التطبيقات المتفق عليها", en: "Agreed brand application designs" },
  },
  {
    title: { ar: "دليل الهوية والتسليم", en: "Brand Guidelines & Delivery" },
    body: { ar: "نوثّق قواعد استخدام الشعار والألوان والخطوط والتطبيقات، وننظم الملفات النهائية مع جلسة تسليم تساعد فريقك على الحفاظ على اتساق الهوية.", en: "We document logo, color, typography and application rules, organize the final files and walk your team through using the identity consistently." },
    deliverable: { ar: "دليل الهوية وحزمة الملفات النهائية", en: "Brand guidelines and final asset library" },
  },
]

export const brandingCapabilities: { title: L10n; body: L10n; items: L10n[] }[] = [
  {
    title: { ar: "شعار يحمل فكرتك", en: "A logo with a clear idea" },
    body: { ar: "علامة مميزة تعبّر عن شخصيتك، وتظل واضحة على شاشة صغيرة أو واجهة كبيرة.", en: "A distinctive mark that expresses your personality and stays clear on a small screen or a large sign." },
    items: [{ ar: "الشعار الأساسي والنسخ البديلة", en: "Primary and alternative lockups" }, { ar: "الرمز والاستخدام أحادي اللون", en: "Brand symbol and monochrome versions" }, { ar: "المساحات الآمنة والحد الأدنى للحجم", en: "Clear space and minimum sizes" }],
  },
  {
    title: { ar: "نظام بصري يجمع كل شيء", en: "A connected visual system" },
    body: { ar: "لغة تصميم تجعل كل جزء من علامتك ينتمي إلى الصورة نفسها، وتمنح فريقك أساسًا يبني عليه.", en: "A design language that makes every part of your brand belong together and gives your team a foundation to build on." },
    items: [{ ar: "لوحة الألوان ونظام الخطوط", en: "Color palette and typography" }, { ar: "العناصر الرسومية وأسلوب الصور", en: "Graphic elements and image direction" }, { ar: "قواعد التكوين وتوزيع المحتوى", en: "Composition and layout principles" }],
  },
  {
    title: { ar: "هوية تعيش في العالم الحقيقي", en: "An identity people encounter" },
    body: { ar: "نختار تطبيقات تتناسب مع نشاطك، ليجد العميل العلامة نفسها في كل مرة يتواصل فيها معك.", en: "We choose applications around your business so customers encounter a consistent brand wherever they meet you." },
    items: [{ ar: "بطاقات العمل والمطبوعات والعروض", en: "Business cards, stationery and decks" }, { ar: "العبوات والملصقات وأكياس المنتجات", en: "Packaging, labels and product bags" }, { ar: "قوالب التواصل والهوية الرقمية", en: "Social templates and digital identity" }],
  },
]

/** Add real projects here. Only published entries appear; no empty portfolio is rendered. */
export type BrandingExample = {
  id: string
  published: boolean
  title: L10n
  category: L10n
  summary: L10n
  cover: { src: string; alt: L10n; width: number; height: number }
  /** Optional locale-neutral case-study path, e.g. /work/project-slug. */
  caseStudyPath?: string
}

export const brandingExamples: BrandingExample[] = []
